import axios from 'axios';
import { getUserTimezone } from '../modules/timezone';
// Import TimeSlot from the types/booking
import { TimeSlot } from '../types/booking';
import i18n from '../i18n';
import { getCustomerToken, getPurchaseGrant } from './customerService';
// Define API URL
const API_URL = `${process.env.REACT_APP_API_URL}/api`;

// Timeout configurations for different operations (in milliseconds)
const TIMEOUTS = {
  BOOKING_CREATE: 30000,      // 30 seconds for booking creation
  BOOKING_UPDATE: 15000,      // 15 seconds for updates
  AVAILABILITY_CHECK: 15000,  // 15 seconds for availability checks
  GENERAL: 10000              // 10 seconds for general operations
};

// Define booking host interface (for co-hosts)
export interface BookingHostInfo {
  id: number;
  bookingId: number;
  userId: number;
  role: string;
  user?: {
    id: number;
    name: string;
    email: string;
  };
}

// Define booking interface
export interface Booking {
  id: number;
  _id?: string; // Keep for backward compatibility during transition
  meetingType: string | any; // ID or populated object
  host: string | any; // ID or populated object
  hostId?: number; // Primary host ID
  attendee: {
    name: string;
    email: string;
    phone?: string;
  };
  startTime: string;
  endTime: string;
  timeZone: string;
  status: 'pending' | 'confirmed' | 'canceled' | 'completed';
  cancelReason?: string;
  location?: string;
  notes?: string;
  videoConference?: {
    provider: string;
    meetingId: string;
    joinUrl: string;
    hostUrl?: string;
    password?: string;
    settings?: Record<string, any>;
  };
  // Meeting model data (preferred over legacy videoConference)
  meeting?: {
    id: number;
    provider: 'zoom' | 'google_meet' | 'microsoft_teams';
    meetingId: string;
    joinUrl: string;
    hostUrl?: string;
    password?: string;
    settings?: Record<string, any>;
    status: 'active' | 'canceled' | 'expired' | 'completed';
  };
  customQuestions?: Array<{
    question: string;
    answer: string;
  }>;
  bookingHosts?: BookingHostInfo[]; // Co-hosts for the meeting
  // Payment fields (snapshot at booking time — populated by the payment backend).
  // paymentMode is 'none' for free bookings; paymentStatus is null/undefined for free bookings.
  paymentStatus?: 'pending' | 'paid' | 'failed' | 'refunded' | 'partially_refunded' | null;
  paymentMode?: 'none' | 'pay_now' | 'pay_later';
  currency?: string | null; // ISO 4217 (e.g. 'JPY', 'USD') for totalPrice
  calendarEventId?: string;
  reminderSent: boolean;
  // Service booking fields
  productId?: number;
  servicePrice?: number;
  providerFee?: number;
  totalPrice?: number;
  product?: {
    id: number;
    title: string;
    price: number;
    durationMinutes: number;
    description?: string;
  };
  // Phone call location fields
  phoneNumber?: string | null;
  countryCode?: string | null;
  // Per-booking meeting details/instructions (editable by host)
  meetingDetails?: string | null;
  // Whether the current user can cancel/reschedule this booking
  // (primary host, MT owner, shared edit access, or admin)
  canManage?: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CreateBookingDto {
  meetingTypeId: string;
  hostId?: string | number; // Host ID (optional, will use meetingType.userId if not provided)
  name: string;
  email: string;
  phone?: string;
  date?: string; // Date string in YYYY-MM-DD format
  startTime: Date | string; // Allow both Date and string
  endTime?: Date | string; // Allow both Date and string
  timeZone: string;
  timezone?: string; // Alternative timezone property
  notes?: string;
  token?: string | null; // Optional auth token
  customQuestions?: Array<{
    question: string;
    answer: string;
  }>;
  // Event waitlist: claim token from a "spot held for you" email link
  waitlistClaimToken?: string;
  [key: string]: any; // Allow additional properties
}

/** Result of resolving a waitlist claim token (public endpoint). */
export interface WaitlistClaimInfo {
  status: 'waiting' | 'notified' | 'booked' | 'expired';
  claimActive: boolean;
  claimExpiresAt?: string | null;
  slotStartTime: string;
  slotEndTime: string;
  guestName: string;
  guestEmail: string;
  guestPhone?: string;
  meetingTypeId: number;
}

// Define a separate TimeSlot interface for API responses
export interface ApiTimeSlot {
  start: string;
  end: string;
}

export interface EventParticipant {
  id: number;
  name: string;
  email: string;
  status: 'pending' | 'confirmed';
  createdAt: string;
}

class BookingService {
  private getAuthHeader(token: string) {
    return {
      headers: {
        Authorization: `Bearer ${token}`
      }
    };
  }

  /**
   * Get all bookings for the current user (as host)
   */
  async getBookings(token: string, query?: { status?: string, startDate?: string, endDate?: string }): Promise<Booking[]> {
    try {
      const response = await axios.get(
        `${API_URL}/bookings`,
        {
          ...this.getAuthHeader(token),
          params: query
        }
      );

      return response.data.bookings;
    } catch (error: any) {
      // Create error with code for upstream handling (Dashboard will use translated message)
      const fetchError: any = new Error(error.response?.data?.message || 'FETCH_BOOKINGS_FAILED');
      fetchError.code = 'FETCH_BOOKINGS_FAILED';
      fetchError.status = error.response?.status;
      throw fetchError;
    }
  }

  /**
   * Get a specific booking by ID
   */
  async getBookingById(token: string, id: string): Promise<Booking> {
    try {
      const response = await axios.get(
        `${API_URL}/bookings/${id}`,
        this.getAuthHeader(token)
      );

      return response.data.booking;
    } catch (error) {
      throw new Error('Failed to fetch booking');
    }
  }

  /**
   * Create a new booking
   */
  async createBooking(booking: CreateBookingDto): Promise<Booking> {
    try {
      // Normalize timezone properties - use timeZone for consistency with the API
      const timeZone = booking.timeZone || booking.timezone || getUserTimezone();

      // Create a copy of the booking data to avoid modifying the original
      const bookingData = {
        ...booking,
        timeZone, // Use normalized timeZone
        language: i18n.language || 'en', // Current language from i18n
      };

      // Remove any duplicate timezone property to avoid confusion
      if (bookingData.timezone && bookingData.timezone !== bookingData.timeZone) {
        delete bookingData.timezone;
      }

      // Extract token for auth header if provided
      const token = bookingData.token;
      delete bookingData.token; // Remove token from the data payload

      // Send startTime as-is - the server will handle timezone conversion
      if (bookingData.startTime instanceof Date) {
        // Store as ISO string for API submission without conversion
        bookingData.startTime = bookingData.startTime.toISOString();
      }

      // Also convert endTime if it exists and is a Date
      if (bookingData.endTime instanceof Date) {
        // Store as ISO string for API submission without conversion
        bookingData.endTime = bookingData.endTime.toISOString();
      }

      // Prepare request config with optional auth header and timeout.
      //
      // A CUSTOMER token takes precedence over the host token here: on a meeting
      // type locked to points or a plan, the server identifies the person
      // spending from this header. A host viewing their own booking page may
      // have both tokens in the browser, and sending the host one would make the
      // server refuse the booking as unauthenticated for entitlement purposes.
      const customerToken = getCustomerToken();

      /**
       * Receipt for a plan or point pack bought moments ago without an account.
       * Sent in the BODY, never as a credential — it names the wallet that
       * purchase created and cannot authenticate anything. Only attached when
       * there is no session, because a signed-in person books as themselves and
       * a leftover receipt must not redirect the spend somewhere else.
       */
      const purchaseGrant = customerToken ? null : getPurchaseGrant();
      if (purchaseGrant) {
        (bookingData as any).entitlementGrantToken = purchaseGrant;
      }

      const config = {
        ...(customerToken
          ? { headers: { Authorization: `Bearer ${customerToken}` } }
          : token
          ? this.getAuthHeader(token)
          : {}),
        timeout: TIMEOUTS.BOOKING_CREATE // 30 second timeout
      };

      const response = await axios.post(
        `${API_URL}/bookings`,
        bookingData,
        config
      );

      // Attach video integration status from top-level response to booking object
      // so the confirmation page can show pending/error states
      const createdBooking = response.data.booking;
      if (response.data.videoIntegrationStatus) {
        createdBooking._videoIntegrationStatus = response.data.videoIntegrationStatus;
        createdBooking._videoIntegrationError = response.data.videoIntegrationError;
      }

      // Phase 3 — Pay Now. Surface paymentRequired + payment intent details so
      // the caller (useBookingSubmission) can route to the PaymentScreen
      // instead of the confirmation page.
      if (response.data.paymentRequired) {
        createdBooking._paymentRequired = true;
        createdBooking._payment = response.data.payment;
      }

      // A booking HELD while the invitee subscribes to the plan that unlocks
      // it. Not a confirmation: the slot is reserved and the caller must run
      // the plan checkout, then confirm THIS booking rather than make another.
      if (response.data.planPurchaseRequired) {
        createdBooking._planPurchaseRequired = true;
        createdBooking._planPurchase = response.data.planPurchase;
      }

      return createdBooking;
    } catch (error: any) {
      // Enhanced error handling with specific error types
      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        throw new Error('TIMEOUT: Request timed out. Please try again.');
      }

      if (!error.response) {
        throw new Error('NETWORK_ERROR: Unable to connect to server. Please check your internet connection.');
      }

      // Pass through server error with error code
      const serverError: any = new Error(error.response?.data?.message || 'Failed to create booking');
      serverError.errorCode = error.response?.data?.errorCode;
      serverError.shouldRefreshCache = error.response?.data?.shouldRefreshCache;
      serverError.status = error.response?.status;
      // Entitlement refusals carry what is missing and, for a plan, which one.
      // The caller turns "subscribe to book this" into a checkout rather than
      // an error message, so these fields must survive the throw.
      serverError.requires = error.response?.data?.requires;
      serverError.planProductId = error.response?.data?.planProductId;
      serverError.pointCost = error.response?.data?.pointCost;
      serverError.pointBalance = error.response?.data?.pointBalance;
      throw serverError;
    }
  }

  /**
   * Confirm a booking that was held while the invitee bought the plan.
   *
   * The server re-resolves the entitlement itself — this only tells it the
   * purchase is done. `entitlementGrantToken` is the receipt from a signed-out
   * purchase; it is ignored when there is a real session.
   */
  async confirmPlanBooking(
    bookingId: number,
    entitlementGrantToken?: string | null
  ): Promise<{ status: string; message?: string; booking?: any }> {
    try {
      const response = await axios.post(
        `${API_URL}/bookings/${bookingId}/confirm-plan`,
        entitlementGrantToken ? { entitlementGrantToken } : {},
        getCustomerToken()
          ? { headers: { Authorization: `Bearer ${getCustomerToken()}` } }
          : {}
      );
      return response.data;
    } catch (error: any) {
      // The hold still stands on anything but an expiry, so the caller can
      // retry rather than lose the slot it is protecting.
      return {
        status: error.response?.data?.error === 'hold_expired' ? 'hold_expired' : 'not_entitled',
        message: error.response?.data?.message,
      };
    }
  }

  /**
   * Update a booking's status (confirm, cancel, etc.)
   */
  async updateBookingStatus(token: string, id: string | number, status: string, cancelReason?: string): Promise<Booking> {
    try {
      // Validate ID
      const bookingId = typeof id === 'string' ? parseInt(id) : id;
      if (isNaN(bookingId) || bookingId <= 0) {
        throw new Error('Invalid booking ID');
      }

      const response = await axios.put(
        `${API_URL}/bookings/${bookingId}/status`,
        { status, cancelReason },
        this.getAuthHeader(token)
      );

      return response.data.booking;
    } catch (error: any) {
      if (error.response?.status === 400) {
        throw new Error('Invalid booking ID');
      } else if (error.response?.status === 404) {
        throw new Error('Booking not found');
      } else if (error.response?.status === 403) {
        throw new Error('You do not have permission to modify this booking');
      }

      throw new Error(error.response?.data?.message || 'Failed to update booking status');
    }
  }

  /**
   * Update booking details (location, video meeting)
   * PUT /api/bookings/:id/details
   */
  async updateBookingDetails(
    token: string,
    bookingId: number,
    updates: {
      location?: string | null;
      videoMeetingUrl?: string | null;
      generateVideoMeeting?: boolean;
      removeVideoMeeting?: boolean;
      phoneNumber?: string | null;
      countryCode?: string | null;
      videoProvider?: string; // Provider override for video generation
      meetingDetails?: string | null; // Per-booking meeting instructions
    }
  ): Promise<{ booking: Booking; meeting?: any; calendarUpdated: boolean; success: boolean }> {
    try {
      const response = await axios.put(
        `${API_URL}/bookings/${bookingId}/details`,
        updates,
        {
          ...this.getAuthHeader(token),
          timeout: TIMEOUTS.BOOKING_UPDATE
        }
      );

      return response.data;
    } catch (error: any) {
      // Check errorCode from response (works for both 400 and 500 errors)
      const errorCode = error.response?.data?.errorCode;
      if (errorCode === 'BOOKING_IN_PAST') {
        throw new Error('BOOKING_IN_PAST');
      } else if (errorCode === 'VIDEO_PROVIDER_DISCONNECTED') {
        throw new Error('VIDEO_PROVIDER_DISCONNECTED');
      } else if (errorCode === 'INVALID_VIDEO_URL') {
        throw new Error('INVALID_VIDEO_URL');
      } else if (errorCode === 'VIDEO_CREATION_FAILED') {
        throw new Error('VIDEO_CREATION_FAILED');
      }

      if (error.response?.status === 400) {
        throw new Error(error.response?.data?.message || 'Invalid request');
      } else if (error.response?.status === 404) {
        throw new Error('Booking not found');
      } else if (error.response?.status === 403) {
        throw new Error('You do not have permission to modify this booking');
      }

      throw new Error(error.response?.data?.message || 'Failed to update booking details');
    }
  }

  /**
   * Reschedule a booking using a reschedule token
   */
  async rescheduleBooking(rescheduleToken: string, bookingData: CreateBookingDto): Promise<Booking> {
    try {
      // Normalize timezone properties
      const timeZone = bookingData.timeZone || bookingData.timezone || getUserTimezone();

      // Create a copy of the booking data
      const rescheduleData = {
        ...bookingData,
        timeZone,
        guestName: bookingData.name,
        guestEmail: bookingData.email,
      };

      // Remove duplicate properties
      delete rescheduleData.token;
      delete rescheduleData.timezone;

      // Format dates as ISO strings
      if (rescheduleData.startTime instanceof Date) {
        rescheduleData.startTime = rescheduleData.startTime.toISOString();
      }
      if (rescheduleData.endTime instanceof Date) {
        rescheduleData.endTime = rescheduleData.endTime.toISOString();
      }

      const response = await axios.post(
        `${API_URL}/bookings/reschedule/${rescheduleToken}`,
        rescheduleData,
        { timeout: TIMEOUTS.BOOKING_CREATE } // 30 second timeout
      );

      return response.data.booking;
    } catch (error: any) {
      // Enhanced error handling
      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        throw new Error('TIMEOUT: Request timed out. Please try again.');
      }

      if (!error.response) {
        throw new Error('NETWORK_ERROR: Unable to connect to server. Please check your internet connection.');
      }

      const serverError: any = new Error(error.response?.data?.message || 'Failed to reschedule booking');
      serverError.errorCode = error.response?.data?.errorCode;
      serverError.shouldRefreshCache = error.response?.data?.shouldRefreshCache;
      serverError.status = error.response?.status;
      throw serverError;
    }
  }

  /**
   * Cancel a booking by token (public endpoint)
   */
  async cancelBookingByToken(token: string, cancelReason?: string): Promise<void> {
    try {
      await axios.post(
        `${API_URL}/bookings/cancel/${token}`,
        { cancelReason }
      );
    } catch (error) {
      throw new Error('Failed to cancel booking');
    }
  }

  /**
   * Join the waitlist for a full event slot.
   * Returns the guest's queue position. Throws with a `code` property the UI
   * can map to a localized message (ALREADY_ON_WAITLIST / WAITLIST_DISABLED / PAST_SLOT).
   */
  async joinWaitlist(data: {
    meetingTypeId: string;
    slotStartTime: string;
    slotEndTime: string;
    guestName: string;
    guestEmail: string;
    guestPhone?: string;
    language?: string;
    timeZone?: string;
  }): Promise<{ position: number }> {
    try {
      const response = await axios.post(`${API_URL}/bookings/waitlist`, data);
      return { position: response.data.position };
    } catch (error: any) {
      const err = new Error(error.response?.data?.message || 'Failed to join waitlist');
      (err as any).code = error.response?.data?.code;
      throw err;
    }
  }

  /**
   * Resolve a waitlist claim token (public) — called when the booking page is
   * opened from a "spot held for you" email link.
   */
  async resolveWaitlistClaim(token: string): Promise<WaitlistClaimInfo | null> {
    try {
      const response = await axios.get(`${API_URL}/bookings/waitlist/claim/${token}`);
      return response.data;
    } catch (error: any) {
      return null; // Unknown/removed token — page falls back to normal booking
    }
  }

  /**
   * Get waitlist count for a specific slot (public endpoint)
   */
  async getWaitlistCount(
    meetingTypeId: string,
    slotStart: string
  ): Promise<number> {
    try {
      const response = await axios.get(
        `${API_URL}/bookings/waitlist/${meetingTypeId}/${encodeURIComponent(slotStart)}/count`
      );
      return response.data.count;
    } catch (error: any) {
      return 0; // Return 0 on error to not block UI
    }
  }

  /**
   * Get full waitlist for a specific slot (admin only)
   */
  async getWaitlist(
    token: string,
    meetingTypeId: string,
    slotStart: string
  ): Promise<Array<{
    id: number;
    notifiedAt?: string | null;
    claimExpiresAt?: string | null;
    guestName: string;
    guestEmail: string;
    guestPhone?: string;
    status: string;
    createdAt: string;
  }>> {
    try {
      const response = await axios.get(
        `${API_URL}/bookings/waitlist/${meetingTypeId}/${encodeURIComponent(slotStart)}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      // API responds { waitlist: [...] }
      return response.data.waitlist || [];
    } catch (error: any) {
      throw new Error('Failed to fetch waitlist');
    }
  }

  /**
   * Get participants for a specific event slot (admin only)
   */
  async getSlotParticipants(
    token: string,
    meetingTypeId: string | number,
    slotStart: string
  ): Promise<{ participants: EventParticipant[]; total: number }> {
    try {
      const encodedStartTime = encodeURIComponent(slotStart);
      const response = await axios.get(
        `${API_URL}/bookings/participants/${meetingTypeId}/${encodedStartTime}`,
        this.getAuthHeader(token)
      );
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to fetch slot participants');
    }
  }
}

const bookingService = new BookingService();
export default bookingService;
